import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  FileText,
  Printer,
  Stethoscope,
  Sparkles,
  TrendingUp,
  XCircle,
  AlertCircle,
  Pencil,
  Plus,
  Check,
  X,
  Trash2,
  CheckCircle,
  Loader2,
} from "lucide-react";
import { formatDate } from "../lib/utils";
import { patientApi } from "../services/api";
import { ConcluziiAvizButton } from "./ConcluziiAvizButton";
import { toast } from "sonner";

// Generare ID simplu pentru itemi noi (fallback dacă backend nu asignează)
const newId = () => `local-${Math.random().toString(36).slice(2, 11)}`;

// Preseturi rapide pentru "Adaugă recomandare" în Referat medical.
// - Text simplu → click direct pentru a adăuga.
// - "Acte medicale" → deschide sub-checkboxuri (utilizatorul bifează una sau mai multe,
//   apoi apasă "Adaugă (N)" pentru a le introduce toate ca recomandări separate).
const RECOMANDARI_PRESETS = [
  {
    id: "reia-efort",
    label: "Va relua efortul specific în mod progresiv sub supraveghere",
    text: "Va relua efortul specific în mod progresiv sub supraveghere",
  },
  {
    id: "acte-medicale",
    label: "Acte medicale",
    subOptions: [
      "Adeverință medicală de la medicul de familie cu istoricul stării de sănătate",
      "Adeverință medicală de la medicul de familie care să ateste luarea în evidență și inițierea sau continuarea tratamentului (acolo unde este cazul — dispensarizarea prin medicul de familie)",
      "Adeverință medicală de la medicul curant care să ateste acordul acestuia pentru practicarea sportului de performanță",
      "Acte medicale care precizează diagnosticul și conduita terapeutică",
    ],
  },
];

// Calcul vârstă
const calculateAge = (birthDate) => {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

// Diferență numerică formatată
const diffNum = (current, previous, decimals = 2) => {
  const c = parseFloat(current);
  const p = parseFloat(previous);
  if (isNaN(c) || isNaN(p)) return null;
  const d = c - p;
  if (Math.abs(d) < Math.pow(10, -decimals)) return null;
  return { delta: d, sign: d > 0 ? "+" : "", abs: Math.abs(d).toFixed(decimals) };
};

// Găsește cea mai recentă intrare din istoric pentru un atelier dat
const getLatestHistoryEntry = (istoric, atelierNum, excludeId = null) => {
  const filtered = (istoric || [])
    .filter((e) => e.atelier_num === atelierNum && e.id !== excludeId)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  return filtered[0] || null;
};

// Construiește analiza dinamică (comparație cu fișa anterioară)
const buildAnalizaDinamica = (patient) => {
  const istoric = patient.istoric_ateliere || [];
  const results = [];

  // --- Atelier 1: compoziție corporală + forță ---
  const prev1 = getLatestHistoryEntry(istoric, 1);
  const a1 = patient.atelier_1 || {};
  if (prev1 && prev1.snapshot) {
    const p = prev1.snapshot;
    const dataPrev = formatDate(prev1.data_completare || prev1.date);
    const changes1 = [];

    // Talie (înălțime) - diferența afișată în cm
    const dTalie = diffNum(a1.talie, p.talie, 3);
    if (dTalie) {
      const cm = (Math.abs(dTalie.delta) * 100).toFixed(1);
      if (dTalie.delta > 0) changes1.push(`a crescut ${cm} cm în înălțime`);
      else changes1.push(`a scăzut ${cm} cm în înălțime`);
    }

    // Greutate
    const dG = diffNum(a1.greutate, p.greutate, 1);
    if (dG) {
      if (dG.delta > 0) changes1.push(`s-a îngrășat ${dG.abs} kg`);
      else changes1.push(`a slăbit ${dG.abs} kg`);
    }

    // IMC
    const dIMC = diffNum(a1.imc, p.imc);
    if (dIMC) {
      const sign = dIMC.delta > 0 ? "+" : "-";
      changes1.push(`I.M.C. ${sign}${dIMC.abs} (de la ${parseFloat(p.imc).toFixed(2)} la ${parseFloat(a1.imc).toFixed(2)})`);
    }

    // TA% (țesut adipos)
    const dTA = diffNum(a1.ta_procent, p.ta_procent);
    if (dTA) {
      const verb = dTA.delta > 0 ? "a crescut" : "a scăzut";
      changes1.push(`procentul țesutului adipos ${verb} cu ${dTA.abs}% (de la ${parseFloat(p.ta_procent).toFixed(2)}% la ${parseFloat(a1.ta_procent).toFixed(2)}%)`);
    }

    // MA% (masă activă)
    const dMA = diffNum(a1.ma_procent, p.ma_procent);
    if (dMA) {
      const verb = dMA.delta > 0 ? "a crescut" : "a scăzut";
      changes1.push(`masa activă ${verb} cu ${dMA.abs}%`);
    }

    // I.F.M.A
    const dIFMA = diffNum(a1.i_fma, p.i_fma);
    if (dIFMA) {
      const verb = dIFMA.delta > 0 ? "s-a îmbunătățit" : "a scăzut";
      changes1.push(`I.F.M.A ${verb} cu ${dIFMA.abs} (de la ${parseFloat(p.i_fma).toFixed(2)} la ${parseFloat(a1.i_fma).toFixed(2)})`);
    }

    if (changes1.length > 0) {
      results.push({
        title: `Compoziție corporală / forță (față de vizita din ${dataPrev})`,
        items: changes1,
      });
    }
  }

  // --- Atelier 2: cardiovascular ---
  const prev2 = getLatestHistoryEntry(istoric, 2);
  const a2 = patient.atelier_2 || {};
  if (prev2 && prev2.snapshot) {
    const p = prev2.snapshot;
    const dataPrev = formatDate(prev2.data_completare || prev2.date);
    const changes2 = [];

    // Ruffier
    const dR = diffNum(a2.ruffier_rezultat, p.ruffier_rezultat);
    if (dR) {
      const verb = dR.delta < 0 ? "s-a îmbunătățit" : "s-a înrăutățit";
      let txt = `indicele Ruffier ${verb} (de la ${parseFloat(p.ruffier_rezultat).toFixed(2)} la ${parseFloat(a2.ruffier_rezultat).toFixed(2)})`;
      if (p.ruffier_interpretare && a2.ruffier_interpretare && p.ruffier_interpretare !== a2.ruffier_interpretare) {
        txt += ` - interpretare trecută de la „${p.ruffier_interpretare}" la „${a2.ruffier_interpretare}"`;
      }
      changes2.push(txt);
    }

    // VO2 relativ
    const dV = diffNum(a2.astrand_vo2_relativ, p.astrand_vo2_relativ);
    if (dV) {
      const verb = dV.delta > 0 ? "s-a îmbunătățit" : "a scăzut";
      let txt = `VO₂ relativ ${verb} cu ${dV.abs} ml/kg/min (de la ${parseFloat(p.astrand_vo2_relativ).toFixed(2)} la ${parseFloat(a2.astrand_vo2_relativ).toFixed(2)})`;
      if (p.astrand_interpretare && a2.astrand_interpretare && p.astrand_interpretare !== a2.astrand_interpretare) {
        txt += ` - interpretare trecută de la „${p.astrand_interpretare}" la „${a2.astrand_interpretare}"`;
      }
      changes2.push(txt);
    }

    // STT
    const dS = diffNum(a2.stt_rezultat, p.stt_rezultat, 0);
    if (dS) {
      const verb = dS.delta < 0 ? "s-a îmbunătățit" : "s-a înrăutățit";
      let txt = `STT ${verb} (de la ${parseFloat(p.stt_rezultat).toFixed(0)} la ${parseFloat(a2.stt_rezultat).toFixed(0)})`;
      if (p.stt_calificativ && a2.stt_calificativ && p.stt_calificativ !== a2.stt_calificativ) {
        txt += ` - calificativ trecut de la „${p.stt_calificativ}" la „${a2.stt_calificativ}"`;
      }
      changes2.push(txt);
    }

    // AV ortostatism
    const dAV = diffNum(a2.av_ortostatism, p.av_ortostatism, 0);
    if (dAV) {
      const verb = dAV.delta > 0 ? "a crescut" : "a scăzut";
      changes2.push(`AV ortostatism ${verb} cu ${dAV.abs} bpm (de la ${p.av_ortostatism} la ${a2.av_ortostatism})`);
    }

    if (changes2.length > 0) {
      results.push({
        title: `Evaluare cardiovasculară (față de vizita din ${dataPrev})`,
        items: changes2,
      });
    }
  }

  // --- Atelier 4: examen clinic pe sisteme ---
  const prev4 = getLatestHistoryEntry(istoric, 4);
  const a4 = patient.atelier_4 || {};
  if (prev4 && prev4.snapshot) {
    const p = prev4.snapshot;
    const dataPrev = formatDate(prev4.data_completare || prev4.date);
    const changes4 = [];

    const sistemeMap = [
      ["tegumente", "Tegumente și mucoase"],
      ["ganglionar", "Sistem ganglionar"],
      ["locomotor", "Aparat locomotor"],
      ["respirator", "Aparat respirator"],
      ["cardiovascular", "Aparat cardiovascular"],
      ["digestiv", "Aparat digestiv"],
      ["urogenital", "Aparat urogenital"],
      ["endocrin", "Sistem endocrin"],
    ];

    sistemeMap.forEach(([k, lab]) => {
      const curSt = a4[`${k}_status`];
      const prevSt = p[`${k}_status`];
      if (curSt && prevSt && curSt !== prevSt) {
        if (curSt === "Modificat" && prevSt === "Normal") {
          changes4.push(`${lab}: era Normal, acum este Modificat`);
        } else if (curSt === "Normal" && prevSt === "Modificat") {
          changes4.push(`${lab}: era Modificat, acum este Normal`);
        }
      }
    });

    // Schimbare în țesut celular subcutanat
    if (p.tesut_status && a4.tesut_status && p.tesut_status !== a4.tesut_status) {
      changes4.push(`Țesut celular subcutanat: trecut de la „${p.tesut_descriere || p.tesut_status}" la „${a4.tesut_descriere || a4.tesut_status}"`);
    }

    if (changes4.length > 0) {
      results.push({
        title: `Examen clinic pe sisteme (față de vizita din ${dataPrev})`,
        items: changes4,
      });
    }
  }

  return results;
};

// Secțiune reutilizabilă (titlu + conținut)
const Section = ({ title, children, className = "" }) => (
  <section className={`mb-5 ${className}`}>
    <h3 className="text-base font-semibold border-b border-slate-300 pb-1 mb-2 text-slate-800">
      {title}
    </h3>
    <div className="text-sm text-slate-700 space-y-1">{children}</div>
  </section>
);

// Rând cu etichetă
const Row = ({ label, value }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex gap-2">
      <span className="text-slate-500 min-w-[180px]">{label}:</span>
      <span className="font-medium">{value}</span>
    </div>
  );
};

// Colectează recomandările dinamice generate din Atelier 4 (checkbox-uri per sistem)
// pentru a le afișa inline în secțiunea "IX. Examen clinic general (Atelier 4)".
// Sincronizat cu logica din ConcluziiAvizButton.collectAtelier4Recomandari.
const collectAtelier4Recs = (a4) => {
  const out = [];
  if (!a4) return out;
  if (a4.locomotor_status === "Modificat") {
    if (a4.loc_repaus) out.push(`Repaus segmentar ${a4.loc_repaus_sapt || "___"} săptămâni`);
    if (a4.loc_investigatii) out.push(`Investigații imagistice suplimentare${a4.loc_investigatii_text ? ": " + a4.loc_investigatii_text : ""}`);
    if (a4.loc_reevaluare) out.push(`Reevaluare în ${a4.loc_reevaluare_sapt || "___"} săptămâni`);
    if (a4.loc_ortopedie_ped) out.push("Consult și tratament de specialitate serviciu Ortopedie pediatrică");
    if (a4.loc_ortopedie) out.push("Consult și tratament de specialitate serviciu Ortopedie");
  }
  if (a4.respirator_status === "Modificat") {
    if (a4.resp_pediatrie) out.push("Consult și tratament de specialitate serviciu Pediatrie");
    if (a4.resp_rx) out.push("RX cord-pulmon");
    if (a4.resp_probe) out.push("Probe ventilatorii");
    if (a4.resp_repaus) out.push(`Repaus, izolare și tratament la domiciliu pe o perioadă de ${a4.resp_repaus_zile || "___"} zile`);
  }
  if (a4.cardiovascular_status === "Modificat") {
    if (a4.cv_cardiologie_ped) out.push("Consult de specialitate serviciu Cardiologie pediatrică");
    if (a4.cv_cardiologie) out.push("Consult de specialitate Cardiologie");
    if (a4.cv_ecocardiografie) out.push("Ecocardiografie (Ecodoppler)");
    if (a4.cv_holter_ekg) out.push("Holter EKG (24 de ore)");
    if (a4.cv_holter_ta) out.push("Holter TA (24 de ore)");
    if (a4.cv_in_observatie) out.push("În observație (suflu sistolic) — reevaluare semestrială");
  }
  if (a4.digestiv_status === "Modificat") {
    if (a4.dig_ecografie) out.push("Ecografie abdominală");
    if (a4.dig_gastro) out.push("Control de specialitate Gastroenterologie");
  }
  if (a4.urogenital_status === "Modificat") {
    if (a4.uro_urologie) out.push("Control de specialitate serviciu Urologie");
    if (a4.uro_nefrologie) out.push("Control de specialitate serviciu Nefrologie");
  }
  if (a4.endocrin_status === "Modificat" && a4.endo_endocrinologie) out.push("Control de specialitate Endocrinologie");
  if (a4.acuitate_vizuala_status === "Modificat") out.push("Consult oftalmologic periodic");
  if (a4.sistem_nervos_status === "Modificat" && a4.sistem_nervos_afectiuni_preexistente)
    out.push("Se cere document medical prin care se atestă acordul medicului curant neurolog pentru sportul de performanță");
  if (a4.sistem_nervos_status === "Modificat" && a4.sistem_nervos_debut_recent) out.push("Consult neurologic");
  if (a4.cavitate_orala_status === "Modificat" && (a4.cavitate_carii_superficiale || a4.cavitate_carii_profunde))
    out.push("Consult și tratament stomatologic (pentru asanarea focarelor infecțioase)");
  if (a4.cavitate_orala_status === "Modificat" && (a4.cavitate_hipertrofie_stanga || a4.cavitate_hipertrofie_dreapta || a4.cavitate_hipertrofie_bilaterala))
    out.push("Consult ORL");
  if (a4.program_individualizat) out.push("Program individualizat de pregătire și antrenament");
  return out;
};

// Construiește analiza medicală narativă
const buildAnalizaMedicala = (patient) => {
  const dp = patient.date_personale || {};
  const ins = patient.info_sportive || {};
  const a1 = patient.atelier_1 || {};
  const a2 = patient.atelier_2 || {};
  const a4 = patient.atelier_4 || {};
  const age = calculateAge(dp.data_nasterii);
  const parts = [];

  // Identificare
  const nume = `${dp.prenume || ""} ${dp.nume || ""}`.trim();
  if (nume) {
    let intro = `Sportivul ${nume}`;
    if (age !== null) intro += `, în vârstă de ${age} ani`;
    if (dp.sex) intro += `, de sex ${dp.sex.toLowerCase()}`;
    if (ins.sport_practicat) {
      intro += `, practicant al sportului ${ins.sport_practicat}`;
      if (ins.categorie) intro += ` (${ins.categorie})`;
      if (ins.club) intro += ` la ${ins.club}`;
    }
    parts.push(intro + ".");
  }

  // Compoziție corporală
  const comp = [];
  if (a1.talie) comp.push(`înălțimea de ${a1.talie} m`);
  if (a1.greutate) comp.push(`greutatea de ${a1.greutate} kg`);
  if (a1.imc) {
    let imcTxt = `un I.M.C. de ${parseFloat(a1.imc).toFixed(2)}`;
    if (a1.imc_interpretare) imcTxt += ` (${a1.imc_interpretare})`;
    comp.push(imcTxt);
  }
  if (a1.ta_procent) comp.push(`un procent al țesutului adipos de ${parseFloat(a1.ta_procent).toFixed(2)}%`);
  if (a1.ma_procent) comp.push(`o masă activă de ${parseFloat(a1.ma_procent).toFixed(2)}%`);
  if (comp.length > 0) {
    parts.push(`Prezintă ${comp.join(", ")}.`);
  }
  if (a1.greutate_optima) {
    parts.push(`Greutatea optimă calculată este de ${parseFloat(a1.greutate_optima).toFixed(1)} kg.`);
  }
  if (a1.recomandare_sport) {
    parts.push(`Din punct de vedere al compoziției corporale: ${a1.recomandare_sport}.`);
  }

  // Forță musculară
  const forta = [];
  if (a1.i_fg) forta.push(`I.F.G = ${parseFloat(a1.i_fg).toFixed(2)}`);
  if (a1.i_fma) forta.push(`I.F.M.A = ${parseFloat(a1.i_fma).toFixed(2)}`);
  if (forta.length > 0) {
    let fortaTxt = `La evaluarea forței musculare: ${forta.join(", ")}`;
    if (a1.i_fma_interpretare) fortaTxt += ` - ${a1.i_fma_interpretare}`;
    parts.push(fortaTxt + ".");
  }

  // Evaluare posturală (din Atelier 1)
  const postProbleme = [];
  if (a1.tulburari_statica === "Da") postProbleme.push("tulburări de statică vertebrală");
  if (a1.asimetrie_umeri === "Da") postProbleme.push("asimetrie de umeri");
  if (a1.asimetrie_torace === "Da") postProbleme.push("asimetrie toracică");
  if (a1.asimetrie_membre === "Da") postProbleme.push("asimetrie de membre inferioare");
  if (a1.genu_valgum === "Da") postProbleme.push("genu valgum");
  if (a1.genu_varum === "Da") postProbleme.push("genu varum");
  if (a1.picior_addus === "Da") postProbleme.push("picior addus");
  if (postProbleme.length > 0) {
    parts.push(`La evaluarea posturală se constată: ${postProbleme.join(", ")}.`);
  } else if (a1.completat) {
    parts.push("La evaluarea posturală nu se constată modificări patologice.");
  }

  // Cardiovascular
  const cv = [];
  if (a2.av_ortostatism) cv.push(`AV ortostatism = ${a2.av_ortostatism} bpm`);
  if (a2.ta_ortostatism) cv.push(`TA ortostatism = ${a2.ta_ortostatism} mmHg`);
  if (a2.av_clinostatism) cv.push(`AV clinostatism = ${a2.av_clinostatism} bpm`);
  if (a2.ta_clinostatism) cv.push(`TA clinostatism = ${a2.ta_clinostatism} mmHg`);
  if (cv.length > 0) {
    parts.push(`La evaluarea cardiovasculară de repaus: ${cv.join(", ")}.`);
  }

  // Test Ruffier
  if (a2.ruffier_rezultat && a2.ruffier_interpretare) {
    parts.push(
      `Testul Ruffier înregistrează un indice de ${parseFloat(a2.ruffier_rezultat).toFixed(2)}, cu interpretare „${a2.ruffier_interpretare}".`
    );
  }

  // Test Astrand
  if (a2.astrand_vo2_relativ && a2.astrand_interpretare) {
    let astr = `Testul Astrand evidențiază un VO₂ relativ de ${parseFloat(a2.astrand_vo2_relativ).toFixed(2)} ml/kg/min (${a2.astrand_interpretare})`;
    if (a2.astrand_conformitate) astr += `, ${a2.astrand_conformitate.toLowerCase()}`;
    parts.push(astr + ".");
  }
  if (a2.astrand_revenire) {
    let rev = `Revenirea post-efort este „${a2.astrand_revenire}"`;
    if (a2.astrand_revenire_recomandare) rev += ` - ${a2.astrand_revenire_recomandare}`;
    parts.push(rev + ".");
  }

  // STT
  if (a2.stt_rezultat && a2.stt_calificativ) {
    parts.push(
      `Indicele STT (Sistolic Tension Time) este ${parseFloat(a2.stt_rezultat).toFixed(0)}, calificat ca „${a2.stt_calificativ}".`
    );
  }

  // Examen clinic pe sisteme (Atelier 4)
  const sistemeMap = [
    ["tegumente", "tegumente și mucoase"],
    ["ganglionar", "sistem ganglionar"],
    ["locomotor", "aparat locomotor"],
    ["respirator", "aparat respirator"],
    ["cardiovascular", "aparat cardiovascular"],
    ["digestiv", "aparat digestiv"],
    ["urogenital", "aparat urogenital"],
    ["endocrin", "sistem endocrin"],
  ];
  const modSisteme = sistemeMap
    .filter(([k]) => a4[`${k}_status`] === "Modificat")
    .map(([, lab]) => lab);

  if (a4.completat || modSisteme.length > 0) {
    if (modSisteme.length === 0) {
      parts.push("La examenul clinic pe sisteme, toate aparatele și sistemele sunt în limite normale.");
    } else {
      parts.push(
        `La examenul clinic pe sisteme se constată modificări la nivelul următoarelor: ${modSisteme.join(", ")}.`
      );
      modSisteme.forEach((lab) => {
        const k = sistemeMap.find(([, l]) => l === lab)?.[0];
        const desc = a4[`${k}_descriere`];
        if (desc) parts.push(`• ${lab.charAt(0).toUpperCase() + lab.slice(1)}: ${desc}`);
      });
    }
  }

  // Țesut celular subcutanat
  if (a4.tesut_descriere) {
    parts.push(`Țesut celular subcutanat: ${a4.tesut_descriere}.`);
  }

  // Istoric medical relevant
  const im = patient.istoric_medical || {};
  const afectiuni = [];
  if (im.boli_cardiace === "Da") afectiuni.push("boli cardiace");
  if (im.astm === "Da") afectiuni.push("astm");
  if (im.diabet === "Da") afectiuni.push("diabet");
  if (im.alergii === "Da") afectiuni.push("alergii");
  if (im.interventii_chirurgicale === "Da") afectiuni.push("intervenții chirurgicale în antecedente");
  if (afectiuni.length > 0) {
    parts.push(`Din istoricul medical se reține: ${afectiuni.join(", ")}.`);
  }
  if (im.antecedente_personale && im.antecedente_personale.trim()) {
    parts.push(`Antecedente medicale personale: ${im.antecedente_personale.trim()}.`);
  }
  if (im.antecedente_familie && im.antecedente_familie.trim()) {
    parts.push(`Antecedente medicale în familie: ${im.antecedente_familie.trim()}.`);
  }

  return parts;
};

// Opțiunile Concluziei (verdict final Referat medical + Concluzii aviz medico-sportiv)
const CONCLUZII = [
  { key: "APT", label: "APT ANTRENAMENTE ȘI COMPETIȚII", iconColor: "text-emerald-600", bgColor: "bg-emerald-50", borderColor: "border-emerald-500", icon: CheckCircle },
  { key: "APT_SUPRAVEGHERE", label: "APT ANTRENAMENTE ȘI COMPETIȚII SUB SUPRAVEGHERE MEDICALĂ", iconColor: "text-amber-600", bgColor: "bg-amber-50", borderColor: "border-amber-500", icon: AlertCircle },
  { key: "APT_INDIVIDUALIZAT", label: "APT ANTRENAMENTE INDIVIDUALIZATE SUB SUPRAVEGHERE", iconColor: "text-orange-600", bgColor: "bg-orange-50", borderColor: "border-orange-500", icon: AlertCircle },
  { key: "INAPT_TEMPORAR", label: "INAPT TEMPORAR", iconColor: "text-red-500", bgColor: "bg-red-50", borderColor: "border-red-500", icon: XCircle },
];

// Construiește lista inițială de recomandări din atelier_1 + atelier_4
// Recomandările unui sistem se includ DOAR dacă sistemul este pe "Modificat"
const buildInitialRecomandari = (patient = {}) => {
  const list = [];
  const a1 = patient.atelier_1 || {};
  const a4 = patient.atelier_4 || {};
  // Atelier 1 — Mobilitate Medie sau Slabă → exerciții de creștere a mobilității
  if (a1.mobilitate === "Medie" || a1.mobilitate === "Slaba") {
    list.push({ id: newId(), text: "Exerciții de creștere a mobilității." });
  }
  // Tegumente — doar dacă status = Modificat
  if (a4.tegumente_status === "Modificat" && a4.teg_dermatologie) {
    list.push({ id: newId(), text: "Se recomandă control și tratament de specialitate serviciu Dermatologie." });
  }
  // Țesut celular subcutanat (independent de status - generate din IMC)
  if (a4.tesut_monitorizare) list.push({ id: newId(), text: "Se recomandă monitorizarea greutății corporale." });
  if (a4.tesut_nutritie) list.push({ id: newId(), text: "Se recomandă consult și tratament de specialitate serviciu Nutriție." });
  if (a4.tesut_endocrin) list.push({ id: newId(), text: "Se recomandă consult și tratament de specialitate serviciu Endocrinologie." });
  // Aparat locomotor — doar dacă Modificat
  if (a4.locomotor_status === "Modificat") {
    if (a4.loc_repaus) list.push({ id: newId(), text: `Se recomandă repaus segmentar ${a4.loc_repaus_sapt || "___"} săptămâni.` });
    if (a4.loc_investigatii) list.push({ id: newId(), text: `Se recomandă investigații imagistice suplimentare${a4.loc_investigatii_text ? ": " + a4.loc_investigatii_text : ""}.` });
    if (a4.loc_reevaluare) list.push({ id: newId(), text: `Se recomandă reevaluare în ${a4.loc_reevaluare_sapt || "___"} săptămâni.` });
    if (a4.loc_ortopedie_ped) list.push({ id: newId(), text: "Se recomandă consult și tratament de specialitate serviciu Ortopedie pediatrică." });
    if (a4.loc_ortopedie) list.push({ id: newId(), text: "Se recomandă consult și tratament de specialitate serviciu Ortopedie." });
  }
  // Aparat respirator — doar dacă Modificat
  if (a4.respirator_status === "Modificat") {
    if (a4.resp_pediatrie) list.push({ id: newId(), text: "Se recomandă consult și tratament de specialitate serviciu Pediatrie." });
    if (a4.resp_rx) list.push({ id: newId(), text: "Se recomandă RX cord-pulmon." });
    if (a4.resp_probe) list.push({ id: newId(), text: "Se recomandă probe ventilatorii." });
    if (a4.resp_repaus) list.push({ id: newId(), text: `Se recomandă repaus, izolare și tratament la domiciliu pe o perioadă de ${a4.resp_repaus_zile || "___"} zile.` });
  }
  // Aparat cardiovascular — doar dacă Modificat
  if (a4.cardiovascular_status === "Modificat") {
    if (a4.cv_cardiologie_ped) list.push({ id: newId(), text: "Se recomandă consult de specialitate serviciu Cardiologie pediatrică." });
    if (a4.cv_cardiologie) list.push({ id: newId(), text: "Se recomandă consult de specialitate Cardiologie." });
    if (a4.cv_ecocardiografie) list.push({ id: newId(), text: "Se recomandă Ecocardiografie (Ecodoppler)." });
    if (a4.cv_holter_ekg) list.push({ id: newId(), text: "Se recomandă Holter EKG (24 de ore)." });
    if (a4.cv_holter_ta) list.push({ id: newId(), text: "Se recomandă Holter TA (24 de ore)." });
  }
  // Aparat digestiv — doar dacă Modificat
  if (a4.digestiv_status === "Modificat") {
    if (a4.dig_ecografie) list.push({ id: newId(), text: "Se recomandă Ecografie abdominală." });
    if (a4.dig_gastro) list.push({ id: newId(), text: "Se recomandă control de specialitate Gastroenterologie." });
  }
  // Aparat urogenital — doar dacă Modificat
  if (a4.urogenital_status === "Modificat") {
    if (a4.uro_urologie) list.push({ id: newId(), text: "Se recomandă control de specialitate serviciu Urologie." });
    if (a4.uro_nefrologie) list.push({ id: newId(), text: "Se recomandă control de specialitate serviciu Nefrologie." });
  }
  // Sistem endocrin — doar dacă Modificat
  if (a4.endocrin_status === "Modificat" && a4.endo_endocrinologie) {
    list.push({ id: newId(), text: "Control de specialitate Endocrinologie." });
  }
  // PIPA — Program Individualizat de Pregătire și Antrenament
  if (a4.program_individualizat) {
    list.push({ id: newId(), text: "Se recomandă program individualizat de pregătire și antrenament." });
  }
  return list;
};

// Construiește lista inițială de observații din atelier 1, 2, 4
const buildInitialObservatii = (patient) => {
  const list = [];
  const a1 = patient.atelier_1 || {};
  const a2 = patient.atelier_2 || {};
  const a4 = patient.atelier_4 || {};
  if (a1.observatii) list.push({ id: newId(), text: `[Atelier 1] ${a1.observatii}` });
  if (a2.observatii) list.push({ id: newId(), text: `[Atelier 2] ${a2.observatii}` });
  if (a4.observatii) list.push({ id: newId(), text: `[Atelier 4] ${a4.observatii}` });
  return list;
};

// Item editabil cu pencil + trash
const EditableItem = ({ item, onChange, onDelete, icon: Icon, iconClass, testIdPrefix }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.text);

  const startEdit = () => {
    setDraft(item.text);
    setEditing(true);
  };

  const saveEdit = () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      // Text gol → șterge itemul (împreună cu iconul)
      onDelete(item.id);
    } else {
      onChange(item.id, trimmed);
    }
    setEditing(false);
  };

  const cancelEdit = () => {
    setDraft(item.text);
    setEditing(false);
  };

  if (editing) {
    return (
      <li className="flex items-start gap-2 p-2 rounded border-2 border-primary/40 bg-white">
        <Icon className={`w-4 h-4 mt-2 flex-shrink-0 ${iconClass}`} />
        <Textarea
          autoFocus
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="flex-1 min-h-[60px]"
          data-testid={`${testIdPrefix}-edit-textarea`}
        />
        <div className="flex flex-col gap-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 w-7 p-0 bg-emerald-50 hover:bg-emerald-100"
            onClick={saveEdit}
            data-testid={`${testIdPrefix}-save`}
            title="Salvează (gol = șterge)"
          >
            <Check className="w-3.5 h-3.5 text-emerald-700" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 w-7 p-0"
            onClick={cancelEdit}
            title="Anulează"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      </li>
    );
  }

  return (
    <li className="flex items-start gap-2 p-2 rounded border bg-white">
      <Icon className={`w-4 h-4 mt-1 flex-shrink-0 ${iconClass}`} />
      <span className="text-sm flex-1">{item.text}</span>
      <div className="flex gap-1 flex-shrink-0 print:hidden">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0"
          onClick={startEdit}
          data-testid={`${testIdPrefix}-edit`}
          title="Editează"
        >
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={() => onDelete(item.id)}
          data-testid={`${testIdPrefix}-delete`}
          title="Șterge"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </li>
  );
};

// Formular adăugare rapidă
const AddNewForm = ({ onAdd, placeholder, testIdPrefix, presets }) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [acteOpen, setActeOpen] = useState(false);
  const [acteSelected, setActeSelected] = useState([]);

  const submit = () => {
    const t = value.trim();
    if (!t) {
      setOpen(false);
      return;
    }
    onAdd(t);
    setValue("");
    setActeOpen(false);
    setActeSelected([]);
    setOpen(false);
  };

  const addPresetText = (txt) => {
    onAdd(txt);
  };

  const closeAll = () => {
    setValue("");
    setActeOpen(false);
    setActeSelected([]);
    setOpen(false);
  };

  if (!open) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mt-2 gap-1 print:hidden"
        onClick={() => setOpen(true)}
        data-testid={`${testIdPrefix}-add-btn`}
      >
        <Plus className="w-3.5 h-3.5" />
        Adaugă
      </Button>
    );
  }
  return (
    <div className="mt-2 space-y-2 print:hidden" data-testid={`${testIdPrefix}-add-panel`}>
      {presets && presets.length > 0 && (
        <div className="rounded-md border bg-slate-50 p-2 space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preseturi rapide</div>
          <div className="flex flex-wrap gap-2">
            {presets.map((p) =>
              p.subOptions ? (
                <Button
                  key={p.id}
                  type="button"
                  size="sm"
                  variant={acteOpen ? "default" : "outline"}
                  onClick={() => setActeOpen((v) => !v)}
                  data-testid={`${testIdPrefix}-preset-${p.id}`}
                  className="gap-1"
                >
                  {p.label}
                </Button>
              ) : (
                <Button
                  key={p.id}
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => addPresetText(p.text)}
                  data-testid={`${testIdPrefix}-preset-${p.id}`}
                >
                  {p.label}
                </Button>
              )
            )}
          </div>
          {acteOpen && (
            <div className="mt-2 space-y-2 rounded border bg-white p-2" data-testid={`${testIdPrefix}-preset-acte-panel`}>
              <div className="text-xs font-medium text-slate-700">Selectează actele medicale necesare:</div>
              {(presets.find((x) => x.id === "acte-medicale")?.subOptions || []).map((opt, idx) => {
                const isChecked = acteSelected.includes(opt);
                const idAttr = `${testIdPrefix}-acte-${idx}`;
                return (
                  <div key={opt} className="flex items-start gap-2">
                    <Checkbox
                      id={idAttr}
                      checked={isChecked}
                      onCheckedChange={(v) => {
                        const on = !!v;
                        setActeSelected((prev) => (on ? [...prev.filter((x) => x !== opt), opt] : prev.filter((x) => x !== opt)));
                      }}
                      data-testid={idAttr}
                    />
                    <Label htmlFor={idAttr} className="text-sm font-normal leading-snug cursor-pointer">
                      {opt}
                    </Label>
                  </div>
                );
              })}
              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  size="sm"
                  disabled={acteSelected.length === 0}
                  onClick={() => {
                    acteSelected.forEach((t) => onAdd(`Acte medicale: ${t}`));
                    setActeSelected([]);
                    setActeOpen(false);
                  }}
                  data-testid={`${testIdPrefix}-acte-add-selected`}
                >
                  Adaugă {acteSelected.length > 0 ? `(${acteSelected.length})` : ""}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="flex gap-2">
        <Input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          className="flex-1"
          data-testid={`${testIdPrefix}-add-input`}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") closeAll();
          }}
        />
        <Button type="button" size="sm" onClick={submit} data-testid={`${testIdPrefix}-add-save`}>
          Adaugă
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={closeAll}>
          Închide
        </Button>
      </div>
    </div>
  );
};

export const ReferatMedicalButton = ({ patient, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Local state pentru Recomandări, Observații, Concluzie
  const [recomandari, setRecomandari] = useState([]);
  const [observatii, setObservatii] = useState([]);
  const [concluzie, setConcluzie] = useState("");
  const [vizaValabilaZile, setVizaValabilaZile] = useState("");
  const [vizaValabilaUnit, setVizaValabilaUnit] = useState("zile"); // "zile" | "luni"
  const [dataEmitere, setDataEmitere] = useState(() => new Date().toISOString().slice(0, 10)); // YYYY-MM-DD
  const [fisaNr, setFisaNr] = useState(""); // Override editabil pentru „Fișa nr." — gol -> fallback pe patient.numar_ordine

  // La deschidere, inițializăm local state
  useEffect(() => {
    if (!open || !patient) return;
    const ref = patient.referat || {};
    if (ref.initialized) {
      setRecomandari(ref.recomandari || []);
      setObservatii(ref.observatii || []);
    } else {
      setRecomandari(buildInitialRecomandari(patient));
      setObservatii(buildInitialObservatii(patient));
    }
    setConcluzie(ref.concluzie || "");
    const z = Number(ref.viza_valabila_zile || 0);
    setVizaValabilaZile(z > 0 ? String(z) : "");
    setVizaValabilaUnit(ref.viza_valabila_unit === "luni" ? "luni" : "zile");
    // Data emiterii — folosim data persistată sau azi ca fallback
    setDataEmitere(ref.data_emitere && /^\d{4}-\d{2}-\d{2}$/.test(ref.data_emitere)
      ? ref.data_emitere
      : new Date().toISOString().slice(0, 10));
    // Fișa nr. — override manual din referat; dacă lipsește -> fallback pe patient.numar_ordine
    setFisaNr(ref.fisa_nr ? String(ref.fisa_nr) : String(patient.numar_ordine || ""));
  }, [open, patient]);

  if (!patient) return null;

  const dp = patient.date_personale || {};
  const ins = patient.info_sportive || {};
  const a1 = patient.atelier_1 || {};
  const a2 = patient.atelier_2 || {};
  const a3 = patient.atelier_3 || {};
  const a4 = patient.atelier_4 || {};
  const a5 = patient.atelier_5 || {};
  const im = patient.istoric_medical || {};
  const covid = patient.info_covid || {};
  const efort = patient.efort_fizic || {};

  const analiza = buildAnalizaMedicala(patient);
  const analizaDinamica = buildAnalizaDinamica(patient);

  // Handler-e pentru Recomandări
  const updateRecomandare = (id, text) => {
    setRecomandari((arr) => arr.map((x) => (x.id === id ? { ...x, text } : x)));
  };
  const deleteRecomandare = (id) => {
    setRecomandari((arr) => arr.filter((x) => x.id !== id));
  };
  const addRecomandare = (text) => {
    setRecomandari((arr) => [...arr, { id: newId(), text }]);
  };

  // Handler-e pentru Observații
  const updateObservatie = (id, text) => {
    setObservatii((arr) => arr.map((x) => (x.id === id ? { ...x, text } : x)));
  };
  const deleteObservatie = (id) => {
    setObservatii((arr) => arr.filter((x) => x.id !== id));
  };
  const addObservatie = (text) => {
    setObservatii((arr) => [...arr, { id: newId(), text }]);
  };

  // Salvare referat medical
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        concluzie,
        recomandari: recomandari.map((r) => ({ id: r.id, text: r.text })),
        observatii: observatii.map((o) => ({ id: o.id, text: o.text })),
        initialized: true,
        data_emitere: dataEmitere,
        viza_valabila_zile: Math.max(0, Number(vizaValabilaZile) || 0),
        viza_valabila_unit: vizaValabilaUnit === "luni" ? "luni" : "zile",
        fisa_nr: fisaNr.trim(),
      };
      const updated = await patientApi.updateReferat(patient.id, payload);
      toast.success("Referat medical salvat!");
      // Snapshot HTML pentru istoric — capturăm blocul printabil
      try {
        const body = document.querySelector('[data-testid="referat-body"]');
        if (body) {
          const html = body.outerHTML;
          const snapUpdated = await patientApi.createReferatSnapshot(
            patient.id,
            html,
            patient.numar_viza || null
          );
          if (onUpdate) onUpdate(snapUpdated);
        } else if (onUpdate) {
          onUpdate(updated);
        }
      } catch (snapErr) {
        console.warn("Snapshot referat failed:", snapErr);
        if (onUpdate) onUpdate(updated);
      }
    } catch (error) {
      console.error(error);
      toast.error("Eroare la salvarea referatului");
    } finally {
      setIsSaving(false);
    }
  };

  // Sincronizare bidirecțională dată: modificarea "Data circuit" din Concluzii
  // aviz medico-sportiv actualizează automat data emiterii referatului (persistat
  // imediat pe backend pentru a rămâne consistent chiar dacă utilizatorul închide
  // dialogul fără să salveze restul referatului).
  const syncDataEmitere = async (iso) => {
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return;
    setDataEmitere(iso);
    try {
      const updated = await patientApi.updateReferat(patient.id, { data_emitere: iso });
      if (onUpdate) onUpdate(updated);
    } catch (e) {
      console.error("[Referat] sync data_emitere failed", e);
    }
  };

  const handlePrint = () => {
    // Deschide o fereastră nouă cu doar conținutul referatului (abordare robustă —
    // window.print() din interiorul unui Radix Dialog cu Portal nu funcționează fiabil
    // pe toate browserele din cauza overlay-ului și a stilurilor @media print conflictuale)
    const body = document.querySelector('[data-testid="referat-body"]');
    if (!body) {
      toast.error("Nu am putut prelua conținutul referatului");
      return;
    }
    // Colectează stylesheets din pagina curentă (Tailwind etc.)
    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map((s) => s.outerHTML)
      .join("\n");
    const html = `<!DOCTYPE html>
<html lang="ro">
<head>
  <meta charset="utf-8" />
  <title>Referat medical - ${(patient.date_personale?.prenume || "") + " " + (patient.date_personale?.nume || "")}</title>
  ${styles}
  <style>
    @page { size: A4; margin: 14mm; }
    body { background: white; color: black; font-family: 'Work Sans', Arial, sans-serif; padding: 0; margin: 0; }
    .print-hidden { display: none !important; }
    button { display: none !important; }
    input, textarea { border: none !important; background: transparent !important; padding: 0 !important; }
  </style>
</head>
<body>${body.innerHTML}</body>
</html>`;
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) {
      toast.error("Pop-up blocat — permite ferestrele pop-up pentru această pagină");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    // Așteaptă să se încarce stilurile, apoi declanșează print-ul
    w.onload = () => {
      setTimeout(() => {
        w.focus();
        w.print();
      }, 400);
    };
  };

  // Numerotare dinamică a secțiunilor Referatului. Fiecare Section rendered
  // primește următorul numeral roman, astfel încât secțiunile condiționale
  // (COVID, Toleranță efort, Ateliere, Analiză dinamică) nu creează goluri.
  const ROMAN_NUMERALS = ["I","II","III","IV","V","VI","VII","VIII","IX","X","XI","XII","XIII","XIV","XV","XVI","XVII","XVIII","XIX","XX"];
  const sectionCtr = { n: 0 };
  const nextRoman = () => ROMAN_NUMERALS[sectionCtr.n++] || String(sectionCtr.n);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="gap-2 flex-1 border-primary/30 hover:bg-primary/5"
          data-testid="referat-btn"
        >
          <Stethoscope className="w-4 h-4" />
          Referat medical
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto print:max-w-full print:max-h-none">
        <DialogHeader className="print:hidden">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Referat medical
            </DialogTitle>
            <div className="flex gap-2 mr-8">
              <ConcluziiAvizButton patient={patient} triggerVariant="default" className="border-0" initialDataCircuit={dataEmitere} onDataCircuitChange={syncDataEmitere} onUpdate={onUpdate} />
              <Button
                size="sm"
                onClick={handleSave}
                disabled={isSaving}
                data-testid="referat-save"
                className="gap-1"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Salvează
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handlePrint}
                data-testid="referat-print"
                className="gap-1"
              >
                <Printer className="w-4 h-4" />
                Printează
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Zona care se printează */}
        <div className="referat-body bg-white p-4 print:p-0" data-testid="referat-body">
          {/* Antet oficial */}
          <div className="border-b-2 border-slate-800 pb-3 mb-5">
            <h1 className="text-2xl font-bold text-center">REFERAT MEDICO-SPORTIV</h1>
            <p className="text-center text-sm text-slate-600 mt-1">
              Fișa nr.{" "}
              <span className="hidden print:inline">{fisaNr || patient.numar_ordine || "—"}</span>
              <input
                type="text"
                value={fisaNr}
                onChange={(e) => setFisaNr(e.target.value)}
                placeholder={String(patient.numar_ordine || "—")}
                className="print:hidden inline-block border-b border-slate-300 bg-transparent px-1 text-xs font-medium w-16 text-center focus:outline-none focus:border-primary"
                data-testid="referat-fisa-nr-input"
              />
              {patient.numar_completare ? (
                <>
                  {" · "}
                  <span data-testid="referat-numar-completare">
                    Nr. formular: <strong>#{patient.numar_completare}</strong>
                  </span>
                </>
              ) : null}
              {" · "}Emis la:{" "}
              <span className="hidden print:inline">{formatDate(dataEmitere)}</span>
              <input
                type="date"
                value={dataEmitere}
                onChange={(e) => syncDataEmitere(e.target.value)}
                className="print:hidden inline-block border-b border-slate-300 bg-transparent px-1 text-xs font-medium focus:outline-none focus:border-primary"
                data-testid="referat-data-emitere-input"
              />
            </p>
          </div>

          {/* Date personale */}
          <Section title={`${nextRoman()}. Date personale`}>
            <Row label="Nume și prenume" value={`${dp.nume || ""} ${dp.prenume || ""}`.trim() || "—"} />
            <Row label="Data nașterii" value={dp.data_nasterii ? formatDate(dp.data_nasterii) : null} />
            <Row label="Sex" value={dp.sex} />
            <Row label="CNP" value={dp.cnp} />
            <Row label="Localitate" value={dp.localitate} />
            <Row label="Telefon" value={dp.telefon} />
          </Section>

          {/* Informații sportive */}
          <Section title={`${nextRoman()}. Informații sportive`}>
            <Row label="Sport practicat" value={ins.sport_practicat} />
            <Row label="Post/Categorie" value={ins.categorie} />
            <Row label="Club" value={ins.club} />
            <Row label="Antrenor" value={ins.antrenor} />
            <Row label="Antrenamente/săptămână" value={ins.antrenamente_saptamanal} />
            <Row label="Vechime în sport" value={ins.vechime} />
          </Section>

          {/* Istoric medical */}
          <Section title={`${nextRoman()}. Istoric medical`}>
            <Row label="Boli cardiace" value={im.boli_cardiace} />
            <Row label="Astm" value={im.astm} />
            <Row label="Diabet" value={im.diabet} />
            <Row label="Alergii" value={im.alergii} />
            <Row label="Intervenții chirurgicale" value={im.interventii_chirurgicale} />
            <Row label="Medicație curentă" value={im.medicatie_curenta} />
            <Row label="Antecedente medicale personale" value={im.antecedente_personale} />
            <Row label="Antecedente în familie" value={im.antecedente_familie} />
            <Row label="Detalii" value={im.detalii} />
          </Section>

          {/* COVID */}
          {(covid.a_avut_covid || covid.vaccinat) && (
            <Section title={`${nextRoman()}. Informații COVID-19`}>
              <Row label="A avut COVID-19" value={covid.a_avut_covid} />
              <Row label="Data infectării" value={covid.data_infectare} />
              <Row label="Formă" value={covid.forma_boala} />
              <Row label="Vaccinat" value={covid.vaccinat} />
              <Row label="Doze" value={covid.doze_vaccin} />
            </Section>
          )}

          {/* Efort fizic */}
          {(efort.dispnee || efort.palpitatii || efort.ameteli) && (
            <Section title={`${nextRoman()}. Toleranță la efort`}>
              <Row label="Dispnee" value={efort.dispnee} />
              <Row label="Palpitații" value={efort.palpitatii} />
              <Row label="Amețeli" value={efort.ameteli} />
              <Row label="Dureri precordiale" value={efort.dureri_precordiale} />
              <Row label="Oboseală la efort" value={efort.oboseala} />
            </Section>
          )}

          {/* Atelier 1 - Compoziție corporală */}
          {(a1.talie || a1.greutate || a1.imc) && (
            <Section title={`${nextRoman()}. Antropologie sportivă-nutriție-dezvoltare (Atelier 1)`}>
              <div className="grid grid-cols-2 gap-x-4">
                <Row label="Talie" value={a1.talie ? `${a1.talie} m` : null} />
                <Row label="Greutate" value={a1.greutate ? `${a1.greutate} kg` : null} />
                <Row label="I.M.C." value={a1.imc ? `${parseFloat(a1.imc).toFixed(2)} (${a1.imc_interpretare || ""})` : null} />
                <Row label="TA %" value={a1.ta_procent ? `${parseFloat(a1.ta_procent).toFixed(2)}%` : null} />
                <Row label="MA %" value={a1.ma_procent ? `${parseFloat(a1.ma_procent).toFixed(2)}%` : null} />
                <Row label="Greutate optimă" value={a1.greutate_optima ? `${parseFloat(a1.greutate_optima).toFixed(1)} kg` : null} />
              </div>
              {a1.recomandare_sport && !/^Pentru calculul Greut/i.test(a1.recomandare_sport) && (
                <p className="mt-2 italic text-slate-600">
                  Recomandare sportivă: {a1.recomandare_sport}
                </p>
              )}
              {a1.recomandari_posturale && (
                <p className="mt-1 italic text-slate-600">
                  Recomandări posturale: {a1.recomandari_posturale}
                </p>
              )}
              {a1.recomandare_nutritie && (
                <p className="mt-1 italic text-slate-600">
                  Recomandare nutriție: {a1.recomandare_nutritie}
                </p>
              )}
              {a1.observatii && (
                <p className="mt-1 italic text-slate-600">Observații: {a1.observatii}</p>
              )}
              <div className="grid grid-cols-2 gap-x-4 mt-2">
                <Row label="I.F.G" value={a1.i_fg ? parseFloat(a1.i_fg).toFixed(2) : null} />
                <Row label="I.F.M.A" value={a1.i_fma ? parseFloat(a1.i_fma).toFixed(2) : null} />
              </div>
              {a1.i_fma_interpretare && a1.i_fma_status !== "incomplete" && a1.i_fma && (
                <p className="mt-1 italic text-slate-600">
                  Interpretare I.F.M.A: {a1.i_fma_interpretare}
                </p>
              )}
            </Section>
          )}

          {/* Atelier 2 - Cardiovascular */}
          {(a2.av_ortostatism || a2.ruffier_rezultat || a2.astrand_vo2_relativ) && (
            <Section title={`${nextRoman()}. Teste de adaptare și evaluare a capacității la efort (Atelier 2)`}>
              <div className="grid grid-cols-2 gap-x-4">
                <Row label="AV ortostatism" value={a2.av_ortostatism ? `${a2.av_ortostatism} bpm` : null} />
                <Row label="TA ortostatism" value={a2.ta_ortostatism} />
                <Row label="AV clinostatism" value={a2.av_clinostatism ? `${a2.av_clinostatism} bpm` : null} />
                <Row label="TA clinostatism" value={a2.ta_clinostatism} />
              </div>
              {a2.ruffier_rezultat && (
                <div className="mt-2 p-2 bg-slate-50 rounded border border-slate-200">
                  <p className="font-medium">Test Ruffier</p>
                  <p className="text-xs">
                    P1={a2.ruffier_p1} · P2={a2.ruffier_p2} · P3={a2.ruffier_p3} · Rezultat={parseFloat(a2.ruffier_rezultat).toFixed(2)} - <strong>{a2.ruffier_interpretare}</strong>
                  </p>
                </div>
              )}
              {a2.astrand_vo2_relativ && (
                <div className="mt-2 p-2 bg-slate-50 rounded border border-slate-200">
                  <p className="font-medium">Test Astrand</p>
                  <p className="text-xs">
                    VO₂ max = {a2.astrand_vo2_max ? parseFloat(a2.astrand_vo2_max).toFixed(2) : "—"} · VO₂ relativ = {parseFloat(a2.astrand_vo2_relativ).toFixed(2)}
                    {" - "}<strong>{a2.astrand_interpretare || "—"}</strong>
                  </p>
                  {a2.astrand_conformitate && <p className="text-xs">{a2.astrand_conformitate}</p>}
                  {a2.astrand_revenire && (
                    <p className="text-xs">
                      Revenire: <strong>{a2.astrand_revenire}</strong>
                      {a2.astrand_revenire_recomandare && ` - ${a2.astrand_revenire_recomandare}`}
                    </p>
                  )}
                </div>
              )}
              {a2.stt_rezultat && (
                <div className="mt-2 p-2 bg-slate-50 rounded border border-slate-200">
                  <p className="font-medium">STT (Sistolic Tension Time)</p>
                  <p className="text-xs">
                    Rezultat = {parseFloat(a2.stt_rezultat).toFixed(0)} - <strong>{a2.stt_calificativ}</strong>
                  </p>
                </div>
              )}
              {/* Recomandări Atelier 2 (Ruffier / Astrand / Observații) */}
              {(() => {
                const ruffierRec = !a2.ruffier_recomandare_dismissed && (
                  a2.ruffier_status === "slab"
                    ? "Monitorizarea sportivului în timpul antrenamentului (adaptare foarte slabă)"
                    : (a2.ruffier_status === "nesatisfacator" ? "Monitorizare puls" : null)
                );
                const astrandRec = a2.astrand_revenire_recomandare && !a2.astrand_recomandare_dismissed
                  ? a2.astrand_revenire_recomandare
                  : null;
                const recs = [ruffierRec, astrandRec].filter(Boolean);
                if (recs.length === 0 && !a2.observatii) return null;
                return (
                  <div className="mt-2 space-y-1">
                    {recs.length > 0 && (
                      <div className="text-sm">
                        <span className="font-medium text-slate-700">Recomandări:</span>
                        <ul className="list-disc pl-5 mt-0.5">
                          {recs.map((r, i) => (
                            <li key={i} className="italic text-slate-700">{r}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {a2.observatii && (
                      <p className="italic text-slate-600">Observații: {a2.observatii}</p>
                    )}
                  </div>
                );
              })()}
            </Section>
          )}

          {/* Atelier 3 - ECG (Explorări funcționale medico-sportive) */}
          {(a3.hr || a3.pr_interval || a3.qrs_duration || a3.qt_interval || a3.qtc_interval ||
            a3.interpretare_schiller || a3.diagnostic_medic || a3.observatii ||
            a3.traseu_limite_normale || a3.revenire_ekg) && (
            <Section title={`${nextRoman()}. Explorări funcționale medico-sportive - ECG (Atelier 3)`}>
              <div className="grid grid-cols-2 gap-x-4">
                <Row label="HR (bpm)" value={a3.hr ? parseFloat(a3.hr).toFixed(0) : null} />
                <Row label="Interval PR (ms)" value={a3.pr_interval ? parseFloat(a3.pr_interval).toFixed(0) : null} />
                <Row label="Durată QRS (ms)" value={a3.qrs_duration ? parseFloat(a3.qrs_duration).toFixed(0) : null} />
                <Row label="Interval QT (ms)" value={a3.qt_interval ? parseFloat(a3.qt_interval).toFixed(0) : null} />
                <Row label="Interval QTc (ms)" value={a3.qtc_interval ? parseFloat(a3.qtc_interval).toFixed(0) : null} />
                <Row label="Ax P (°)" value={a3.p_axis !== undefined && a3.p_axis !== null && a3.p_axis !== "" ? parseFloat(a3.p_axis).toFixed(0) : null} />
                <Row label="Ax QRS (°)" value={a3.qrs_axis !== undefined && a3.qrs_axis !== null && a3.qrs_axis !== "" ? parseFloat(a3.qrs_axis).toFixed(0) : null} />
                <Row label="Ax T (°)" value={a3.t_axis !== undefined && a3.t_axis !== null && a3.t_axis !== "" ? parseFloat(a3.t_axis).toFixed(0) : null} />
              </div>
              {a3.interpretare_schiller && (
                <p className="mt-2 text-sm">
                  <span className="font-medium text-slate-700">Interpretare Schiller:</span> {a3.interpretare_schiller}
                </p>
              )}
              {a3.diagnostic_medic && (
                <p className="mt-1 text-sm">
                  <span className="font-medium text-slate-700">Diagnostic medic:</span> {a3.diagnostic_medic}
                </p>
              )}
              {a3.traseu_limite_normale && (
                <p className="mt-1 text-sm italic text-slate-600">
                  Traseu în limite normale cu AV: {a3.traseu_limite_normale_av ?? "___"}
                </p>
              )}
              {a3.revenire_ekg && (
                <p className="mt-1 text-sm italic text-slate-600">
                  Revenire la EKG la {a3.revenire_ekg_zile ?? "___"} zile
                </p>
              )}
              {a3.observatii && (
                <p className="mt-1 italic text-slate-600">Observații: {a3.observatii}</p>
              )}
            </Section>
          )}

          {/* Atelier 4 - Examen clinic */}
          {a4 && (
            <Section title={`${nextRoman()}. Examen clinic general (Atelier 4)`}>
              <div className="space-y-1 text-sm">
                {a4.tesut_descriere && (
                  <div className="flex gap-2">
                    <span className="text-slate-500 min-w-[180px]">Țesut celular subcutanat:</span>
                    <span>{a4.tesut_descriere}</span>
                  </div>
                )}
                {[
                  ["tegumente", "Tegumente și mucoase", "Normal colorate"],
                  ["ganglionar", "Sistem ganglionar", "Ganglioni periferici nepalpabili"],
                  ["locomotor", "Aparat locomotor", "Integru morfofuncțional"],
                  ["respirator", "Aparat respirator", "Torace normal conformat, mișcări respiratorii egale și simetrice, murmur vezicular prezent bilateral, fără zgomote supraadăugate"],
                  ["cardiovascular", "Aparat cardiovascular", "Șoc apexian spațiu 5 IC stâng pe linia medioclaviculară, zgomote cardiace ritmice, fără sufluri, artere periferice permeabile"],
                  ["digestiv", "Aparat digestiv", "Abdomen mobil cu respirația, nedureros spontan sau la palpare, ficat la rebord, splină nepalpabilă, apetit prezent, tranzit păstrat"],
                  ["urogenital", "Aparat urogenital", "Loje renale libere, rinichi nepalpabili, Giordano negativ bilateral, micțiuni fiziologice"],
                  ["endocrin", "Sistem endocrin", "Normal clinic"],
                ].map(([k, lab, normalText]) => {
                  const st = a4[`${k}_status`];
                  const desc = a4[`${k}_descriere`];
                  if (!st) return null;
                  const isModif = st === "Modificat";
                  return (
                    <div key={k} className="flex gap-2 items-start">
                      <span className="text-slate-500 min-w-[180px] flex-shrink-0">{lab}:</span>
                      <span className="flex-1">
                        {isModif ? (
                          <span className="flex items-start gap-1.5">
                            <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                            <span>
                              <Badge variant="destructive" className="text-xs mr-2">Modificat</Badge>
                              {desc && <em className="text-slate-700">{desc}</em>}
                            </span>
                          </span>
                        ) : (
                          <span className="text-slate-700">{normalText}</span>
                        )}
                        {k === "cardiovascular" && !isModif && (a2.av_clinostatism || a2.ta_clinostatism) && (
                          <span className="block text-xs text-slate-600 mt-0.5">
                            {a2.av_clinostatism ? `AV clinostatism: ${a2.av_clinostatism} bpm` : ""}
                            {a2.av_clinostatism && a2.ta_clinostatism ? " · " : ""}
                            {a2.ta_clinostatism ? `TA clinostatism: ${a2.ta_clinostatism}` : ""}
                          </span>
                        )}
                        {k === "cardiovascular" && isModif && a4.cv_in_observatie && (
                          <span className="block text-xs text-amber-700 mt-0.5 italic">
                            În observație (suflu sistolic) — reevaluare semestrială
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
              {/* Recomandări Atelier 4 (checkbox-uri per sistem) + observații */}
              {(() => {
                const recs = collectAtelier4Recs(a4);
                if (recs.length === 0 && !a4.observatii) return null;
                return (
                  <div className="mt-2 space-y-1">
                    {recs.length > 0 && (
                      <div className="text-sm">
                        <span className="font-medium text-slate-700">Recomandări:</span>
                        <ul className="list-disc pl-5 mt-0.5">
                          {recs.map((r, i) => (
                            <li key={i} className="italic text-slate-700">{r}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {a4.observatii && (
                      <p className="italic text-slate-600">Observații: {a4.observatii}</p>
                    )}
                  </div>
                );
              })()}
            </Section>
          )}

          {/* Atelier 5 - Analize medicale de laborator */}
          {(() => {
            // Construim structuri pentru sindroame biologice + sub-recomandări bifate
            const sindroame = [];
            if (a5.sindrom_citoliza_hepatica) {
              const subs = [];
              if (a5.sch_in_observatie) subs.push("În observație");
              if (a5.sch_deconditionare_temporara) subs.push("Decondiționare temporară de efort intens/susținut (reducerea încărcării)");
              if (a5.sch_control_transaminaze) subs.push("Control transaminaze peste 15 zile");
              if (a5.sch_investigatii_suplimentare) subs.push("Necesită investigații suplimentare");
              sindroame.push({ nume: "Sindrom de citoliza hepatica (TGO/TGP crescute)", subs });
            }
            if (a5.sindrom_inflamator_biologic) {
              const subs = [];
              if (a5.sib_dispensarizare_mf) subs.push("Dispensarizare prin medicul de familie (pentru investigații suplimentare și tratament)");
              if (a5.sib_efort_adaptat) subs.push("Efort adaptat (evitarea eforturilor maximale până la identificarea etiologică)");
              if (a5.sib_in_observatie) subs.push("În observație");
              sindroame.push({ nume: "Sindrom inflamator biologic (VSH crescut)", subs });
            }
            if (a5.sindrom_rhabdomioliza) {
              const subs = [];
              if (a5.sr_repeta_ck) subs.push("Repetă dozare CK la 15 zile");
              if (a5.sr_in_observatie) subs.push("În observație");
              if (a5.sr_deconditionare_totala) subs.push("Decondiționare totală de efort (3-5 zile), urmată de reintroducerea treptată a efortului aerobic, hidratare orală susținută 3-4 ore pe zi (protecție renală); reînceperea activității susținute (conform programului de antrenament) se va face numai după normalizarea CK-ului");
              sindroame.push({ nume: "Sindrom de rhabdomioliza de suprasolicitare (hiperCKemie izolată de efort)", subs });
            }
            if (a5.sindrom_anemic) {
              const subs = [];
              if (a5.sa_in_observatie) subs.push("În observație");
              if (a5.sa_dispensarizare_mf) subs.push("Dispensarizare prin medicul de familie (pentru investigații suplimentare, inițierea tratamentului)");
              if (a5.sa_limitare_eforturi) subs.push("Limitarea eforturilor de mare intensitate, anaerobe, până la corectarea anemiei");
              sindroame.push({ nume: "Sindrom anemic / suspiciune beta-talasemie minoră", subs });
            }
            const hasContent = a5.concluzii || a5.recomandari || a5.observatii || (a5.analize_files && a5.analize_files.length > 0) || sindroame.length > 0 || a5.analize_in_limite_normale;
            if (!hasContent) return null;
            return (
              <Section title={`${nextRoman()}. Analize medicale de laborator (Atelier 5)`}>
                <div className="space-y-2 text-sm">
                  {a5.analize_in_limite_normale && (
                    <div className="text-emerald-700 font-medium">
                      ✔ Analize medicale de laborator în limite normale
                    </div>
                  )}
                  {sindroame.length > 0 && (
                    <div>
                      <span className="font-medium text-slate-700">Sindroame biologice detectate:</span>
                      <ul className="list-disc pl-6 mt-1 space-y-1">
                        {sindroame.map((s, i) => (
                          <li key={i}>
                            <strong>{s.nume}</strong>
                            {s.subs.length > 0 && (
                              <ul className="list-[circle] pl-6 mt-0.5">
                                {s.subs.map((r, j) => (
                                  <li key={j}>{r}</li>
                                ))}
                              </ul>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {a5.concluzii && (
                    <div>
                      <span className="font-medium text-slate-700">Concluzii:</span>{" "}
                      <span className="whitespace-pre-wrap">{a5.concluzii}</span>
                    </div>
                  )}
                  {a5.recomandari && (
                    <div>
                      <span className="font-medium text-slate-700">Recomandări:</span>{" "}
                      <span className="whitespace-pre-wrap">{a5.recomandari}</span>
                    </div>
                  )}
                  {a5.observatii && (
                    <div className="italic text-slate-600">
                      Observații: {a5.observatii}
                    </div>
                  )}
                  {a5.analize_files && a5.analize_files.length > 0 && (
                    <p className="text-xs text-slate-500">
                      {a5.analize_files.length} fișier{a5.analize_files.length === 1 ? "" : "e"} de analize atașate în fișă.
                    </p>
                  )}
                </div>
              </Section>
            );
          })()}

          {/* Recomandări - editabile + adăugabile */}
          <section className="mb-5 mt-4" data-testid="referat-recomandari-section">
            <h3 className="text-base font-semibold border-b border-slate-300 pb-1 mb-3 text-slate-800">
              {nextRoman()}. Recomandări
              {recomandari.length > 0 && (
                <Badge variant="secondary" className="ml-2 text-xs">{recomandari.length}</Badge>
              )}
            </h3>
            {recomandari.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Nicio recomandare.</p>
            ) : (
              <ul className="space-y-2" data-testid="referat-recomandari-list">
                {recomandari.map((item) => (
                  <EditableItem
                    key={item.id}
                    item={item}
                    onChange={updateRecomandare}
                    onDelete={deleteRecomandare}
                    icon={XCircle}
                    iconClass="text-red-500"
                    testIdPrefix={`reco-${item.id}`}
                  />
                ))}
              </ul>
            )}
            <AddNewForm
              onAdd={addRecomandare}
              placeholder="Scrie o recomandare nouă..."
              testIdPrefix="reco"
              presets={RECOMANDARI_PRESETS}
            />
          </section>

          {/* Recomandări generale - statice, valabile pentru toți sportivii */}
          <section
            className="mb-5 p-4 rounded-lg border-2 border-emerald-300 bg-emerald-50/60 print:border-emerald-700 print:bg-white"
            data-testid="referat-recomandari-generale-section"
          >
            <h3 className="text-base font-semibold border-b border-emerald-300 pb-1 mb-3 text-emerald-800 print:text-black">
              Recomandări generale
            </h3>
            <ul className="space-y-3 text-sm text-emerald-900 leading-relaxed print:text-black">
              <li className="flex gap-2">
                <span className="font-semibold shrink-0">•</span>
                <span>
                  <strong>Recomandări de nutriție:</strong> Alimentație normocalorică, hiperprotidică și conținut redus de grăsimi asociate în vederea creșterii masei active, normoglucidică, vitaminizată natural (bogată în fructe, legume proaspete), sucuri naturale (fără adaos de zahăr, fosfați, cofeină), cereale integrale fibre. Hidratare corespunzătoare corelată cu pierderile (transpirația) alimentație și uneori suplimente alimentare (proteine, aminoacizi); în sezonul cald trebuie avut în vedere pierderea de electroliți, astfel este necesară și folosirea soluțiilor de hidratare (Gatorade, Litorsal).
                </span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold shrink-0">•</span>
                <span>
                  Este <strong>INTERZISĂ</strong> folosirea băuturilor energizante precum și a substanțelor psihoactive, pentru că pot cauza alterarea stării de sănătate și a antrenamentelor sportive pe termen lung.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold shrink-0">•</span>
                <span>
                  Pentru administrarea suplimentelor alimentare este de preferat consultarea inițială a listelor cu substanțe interzise (ANAD, verifică <strong>portal.anad.gov.ro</strong>) pentru a evita efectele adverse precum și descalificarea din competițiile sportive.
                </span>
              </li>
            </ul>
          </section>

          {/* Observații - editabile + adăugabile */}
          <section className="mb-5" data-testid="referat-observatii-section">
            <h3 className="text-base font-semibold border-b border-slate-300 pb-1 mb-3 text-slate-800">
              Observații
              {observatii.length > 0 && (
                <Badge variant="secondary" className="ml-2 text-xs">{observatii.length}</Badge>
              )}
            </h3>
            {observatii.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Nicio observație.</p>
            ) : (
              <ul className="space-y-2" data-testid="referat-observatii-list">
                {observatii.map((item) => (
                  <EditableItem
                    key={item.id}
                    item={item}
                    onChange={updateObservatie}
                    onDelete={deleteObservatie}
                    icon={AlertCircle}
                    iconClass="text-yellow-500"
                    testIdPrefix={`obs-${item.id}`}
                  />
                ))}
              </ul>
            )}
            <AddNewForm
              onAdd={addObservatie}
              placeholder="Scrie o observație nouă..."
              testIdPrefix="obs"
            />
          </section>

          {/* Analiză medicală - concluzia */}
          {analiza.length > 0 && (
            <section className="mb-5 mt-6 p-4 rounded-lg border-2 border-primary/30 bg-primary/5 print:border-black print:bg-white">
              <h3 className="text-base font-semibold mb-3 flex items-center gap-2 text-primary print:text-black">
                <Sparkles className="w-4 h-4 print:hidden" />
                {nextRoman()}. Analiză medicală (concluzie)
              </h3>
              <div className="text-sm text-slate-800 space-y-2 leading-relaxed">
                {analiza.map((p, i) => (
                  <p key={i} className={p.startsWith("•") ? "pl-4" : ""}>{p}</p>
                ))}
              </div>
            </section>
          )}

          {/* Analiză dinamică - comparație față de vizita precedentă */}
          {analizaDinamica.length > 0 && (
            <section
              className="mb-5 p-4 rounded-lg border-2 border-amber-400/40 bg-amber-50/40 print:border-black print:bg-white"
              data-testid="referat-analiza-dinamica"
            >
              <h3 className="text-base font-semibold mb-3 flex items-center gap-2 text-amber-800 print:text-black">
                <TrendingUp className="w-4 h-4 print:hidden" />
                {nextRoman()}. Analiză dinamică (evoluție față de vizita precedentă)
              </h3>
              <div className="text-sm text-slate-800 space-y-3 leading-relaxed">
                {analizaDinamica.map((grp, i) => (
                  <div key={i}>
                    <p className="font-semibold text-slate-700 mb-1">{grp.title}:</p>
                    <ul className="list-disc ml-5 space-y-1">
                      {grp.items.map((item, j) => (
                        <li key={j}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Concluzii - calificativ final medic */}
          <section className="mb-5 p-4 rounded-lg border-2 border-slate-400 bg-slate-50 print:border-black print:bg-white" data-testid="referat-concluzii-section">
            <h3 className="text-base font-semibold mb-3 text-slate-800">
              {nextRoman()}. Concluzii
            </h3>
            <p className="text-xs text-slate-600 mb-3 print:hidden">
              Selectează calificativul final al examenului medico-sportiv:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {CONCLUZII.map((c) => {
                const Icon = c.icon;
                const selected = concluzie === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setConcluzie(selected ? "" : c.key)}
                    className={`flex items-center gap-2 p-3 rounded-lg border-2 text-left transition print:hidden ${
                      selected
                        ? `${c.bgColor} ${c.borderColor} shadow-sm`
                        : "bg-white border-slate-200 hover:border-slate-400"
                    }`}
                    data-testid={`concluzie-${c.key}`}
                  >
                    <Icon className={`w-5 h-5 flex-shrink-0 ${selected ? c.iconColor : "text-slate-400"}`} />
                    <span className={`text-sm font-medium ${selected ? c.iconColor : "text-slate-700"}`}>
                      {c.label}
                    </span>
                  </button>
                );
              })}
            </div>
            {/* Concluzia selectată - afișată pentru print */}
            {concluzie && (
              <div className="mt-3 hidden print:block">
                <p className="text-lg font-bold">
                  Calificativ final: {CONCLUZII.find((c) => c.key === concluzie)?.label}
                </p>
              </div>
            )}
            {/* Concluzia selectată pentru ecran */}
            {concluzie && (
              <div className="mt-3 print:hidden" data-testid="referat-concluzie-afisata">
                {(() => {
                  const c = CONCLUZII.find((x) => x.key === concluzie);
                  if (!c) return null;
                  const Icon = c.icon;
                  return (
                    <div className={`flex items-center gap-2 p-3 rounded-lg border-2 ${c.bgColor} ${c.borderColor}`}>
                      <Icon className={`w-6 h-6 ${c.iconColor}`} />
                      <span className={`font-bold ${c.iconColor}`}>
                        Calificativ final: {c.label}
                      </span>
                    </div>
                  );
                })()}
              </div>
            )}
          </section>

          {/* Semnătura */}
          <div className="mt-10 flex justify-between items-start text-sm print:mt-16 gap-4 flex-wrap">
            <div>
              <p className="text-slate-600">Data:</p>
              <p>{formatDate(dataEmitere)}</p>
            </div>
            <div className="flex-1 min-w-[240px] max-w-sm text-center" data-testid="viza-valabila-container">
              <p className="text-slate-600">Viza valabilă</p>
              <div className="inline-flex items-baseline gap-2 mt-1">
                <input
                  type="number"
                  min="0"
                  max="999"
                  value={vizaValabilaZile}
                  onChange={(e) => setVizaValabilaZile(e.target.value.replace(/[^\d]/g, ""))}
                  placeholder="—"
                  className="w-16 border-b border-slate-400 bg-transparent text-center font-semibold outline-none focus:border-primary print:border-black"
                  data-testid="viza-valabila-zile"
                />
                <div className="inline-flex rounded-md border overflow-hidden text-xs print:hidden">
                  <button
                    type="button"
                    onClick={() => setVizaValabilaUnit("zile")}
                    className={`px-2 py-0.5 transition ${vizaValabilaUnit === "zile" ? "bg-primary text-primary-foreground" : "bg-white hover:bg-muted"}`}
                    data-testid="viza-unit-zile"
                  >
                    zile
                  </button>
                  <button
                    type="button"
                    onClick={() => setVizaValabilaUnit("luni")}
                    className={`px-2 py-0.5 border-l transition ${vizaValabilaUnit === "luni" ? "bg-primary text-primary-foreground" : "bg-white hover:bg-muted"}`}
                    data-testid="viza-unit-luni"
                  >
                    luni
                  </button>
                </div>
                <span className="hidden print:inline">{vizaValabilaUnit}</span>
              </div>
              {vizaValabilaZile && Number(vizaValabilaZile) > 0 && (
                <p className="text-xs text-slate-500 mt-1" data-testid="viza-valabila-pana-la">
                  până la {(() => {
                    const d = dataEmitere && /^\d{4}-\d{2}-\d{2}$/.test(dataEmitere)
                      ? new Date(dataEmitere + "T00:00:00")
                      : new Date();
                    if (vizaValabilaUnit === "luni") {
                      d.setMonth(d.getMonth() + Number(vizaValabilaZile));
                    } else {
                      d.setDate(d.getDate() + Number(vizaValabilaZile));
                    }
                    return formatDate(d.toISOString());
                  })()}
                </p>
              )}
              <p className="text-xs text-slate-500 mt-1 print:hidden">
                Se propagă automat la „Valabil până la&rdquo; în Concluzii aviz medico-sportiv.
              </p>
            </div>
            <div className="text-right">
              <p className="text-slate-600">Medic specialist:</p>
              <div className="mt-8 border-t border-slate-400 w-48"></div>
              <p className="text-xs text-slate-500 mt-1">Semnătura și parafa</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
